import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import type { z } from "zod";

import { loginRequestSchema, type LoginRequest } from "@sendsms/shared";
import { api } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import { meQueryKey } from "@/lib/auth/context";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
  CardFooter,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

type LocationState = { from?: string } | null;

type LoginFormValues = z.input<typeof loginRequestSchema>;

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [showPassword, setShowPassword] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const returnTo = (location.state as LocationState)?.from ?? "/dashboard";

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: "", password: "", remember: false },
    mode: "onSubmit",
  });

  const loginMutation = useMutation({
    mutationFn: (body: LoginRequest) => api.login(body),
    onSuccess: async (response) => {
      // CRITICAL: Navigate BEFORE invalidating /me to avoid a race condition
      // where AuthContext.status flips to "needs_mfa" while we're still on
      // /login and RequireGuest auto-redirects (potentially to the wrong place).
      // See RegisterPage for the full explanation.
      if (response.requires_mfa) {
        navigate(response.mfa_enabled === false ? "/mfa/setup" : "/mfa/verify", { replace: true }); // MFA-2: unconfirmed enrollment -> setup, never a verify page it cannot pass
      } else {
        navigate(returnTo, { replace: true });
      }
      // QC-LOGOUT-1: a new session starts with an empty cache (covers sessions that ended without the Sign out button).
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
    onError: (err: unknown) => {
      setTopError(null);
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) {
        setTopError(translateApiError(t, err));
      }
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    setTopError(null);
    loginMutation.mutate(data as LoginRequest);
  });

  const emailError = form.formState.errors.email?.message
    ? translateFieldError(t, form.formState.errors.email.message)
    : undefined;
  const passwordError = form.formState.errors.password?.message
    ? translateFieldError(t, form.formState.errors.password.message)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.login.title")}</CardTitle>
        <CardSubtitle>{t("auth.login.subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t("auth.login.email_label")}
            type="email"
            autoComplete="email"
            autoFocus
            required
            {...form.register("email")}
            error={emailError}
          />

          <Input
            label={t("auth.login.password_label")}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            {...form.register("password")}
            error={passwordError}
            trailingElement={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? t("common.hide") : t("common.show")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-muted focus:outline-none focus-visible:shadow-focus"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            }
          />

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-caption text-brand-500 hover:underline focus:outline-none focus-visible:shadow-focus rounded-sm"
            >
              {t("auth.login.forgot_password")}
            </Link>
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={loginMutation.isPending}
          >
            {t("auth.login.submit")}
          </Button>
        </form>
      </CardBody>

      <CardFooter>
        <p className="text-caption text-center text-ink-muted">
          {t("auth.login.no_account")}{" "}
          <Link
            to="/register"
            className="font-medium text-brand-500 hover:underline focus:outline-none focus-visible:shadow-focus rounded-sm"
          >
            {t("auth.login.create_account")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
